// src/pages/Trips.js

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  MapPin, Calendar, X, Briefcase, User,
  ArrowRight, Sparkles, Loader2,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { trips as tripsApi, zones as zonesApi, reasons as reasonsApi, user as userApi } from '../services/api';
import { autoClassifyAll, resolveReasonId } from '../utils/autoClassify';

// ─── Brand colours ────────────────────────────────────────────────────────────
const PRIMARY    = '#00ACC1';
const BIZ_COLOR  = '#FF9800';
const PRIV_COLOR = '#4CAF50';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtKm = (km) => km ? parseFloat(km).toFixed(1) + ' km' : '—';

const fmtTime = (str) => str
  ? new Date(str).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
  : '';

const fmtDateLabel = (key) =>
  new Date(key + 'T12:00:00').toLocaleDateString('en-ZA', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });

// Group trips into [[dateKey, [trips]], ...] newest date first
const groupByDate = (trips) => {
  const groups = {};
  trips.forEach(t => {
    const d   = new Date(t.start_time);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });
  return Object.entries(groups)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, items]) => [key, items.sort((a, b) => new Date(a.start_time) - new Date(b.start_time))]);
};

// ─── Back to top ──────────────────────────────────────────────────────────────
const BackToTop = ({ theme }) => {
  const [v, setV] = useState(false);
  useEffect(() => {
    const fn = () => setV(window.scrollY > 400);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg hover:scale-110 transition-all duration-500 ${v ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}
      style={{ background: `linear-gradient(135deg, ${PRIMARY}, #00838f)`, boxShadow: '0 4px 14px rgba(0,172,193,0.4)' }}
    >
      <ChevronUp size={24} />
    </button>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
const Card = ({ children, className = '', theme }) => (
  <div
    className={`rounded-2xl border transition-all ${className}`}
    style={{
      backgroundColor: theme.card,
      borderColor:     theme.border,
      boxShadow:       theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)',
    }}
  >
    {children}
  </div>
);

// ─── Trip card ────────────────────────────────────────────────────────────────
const TripCard = ({ trip, theme, onClick }) => {
  const isBiz       = trip.category === 'business';
  const accentColor = trip.category ? (isBiz ? BIZ_COLOR : PRIV_COLOR) : theme.textMuted;
  const startLabel  = trip.start_zone_name || trip.start_address || null;
  const endLabel    = trip.end_zone_name   || trip.end_address   || null;

  return (
    <div
      onClick={onClick}
      className="relative flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl cursor-pointer transition-all"
      style={{
        backgroundColor: theme.card,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      {/* Category bar */}
      <div className="w-1 self-stretch rounded-full shrink-0"
        style={{ backgroundColor: accentColor, minHeight: 32 }} />

      {/* Route */}
      <div className="flex-1 min-w-0 py-0.5">
        <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1 min-w-0">
          <span className="text-[11px] sm:text-xs font-medium truncate" style={{ color: theme.text }}>
            {startLabel || 'Unknown start'}
          </span>
          <ArrowRight size={9} style={{ color: theme.textMuted, flexShrink: 0 }} />
          <span className="text-[11px] sm:text-xs font-medium truncate" style={{ color: theme.text }}>
            {endLabel || 'Unknown end'}
          </span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <span className="text-[10px] sm:text-[11px] font-semibold" style={{ color: theme.text }}>
            {fmtKm(trip.distance_km)}
          </span>
          <span className="text-[9px] sm:text-[10px]" style={{ color: theme.textMuted }}>
            {fmtTime(trip.start_time)}
          </span>
         {(trip.reason_detail || trip.reason_name) && (
  <span className="text-[9px] sm:text-[10px] truncate max-w-[120px]" style={{ color: theme.textMuted }}>
    {trip.reason_detail || trip.reason_name}
  </span>
)}
        </div>
      </div>

      {/* Badge + chevron */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {trip.category ? (
          <span
            className="text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full font-medium"
            style={{
              backgroundColor: `${accentColor}18`,
              color: accentColor,
            }}
          >
            {isBiz ? 'Biz' : 'Priv'}
          </span>
        ) : (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: theme.background, color: theme.textMuted }}>
            ?
          </span>
        )}
        <ChevronRight size={12} style={{ color: theme.textMuted }} className="hidden sm:block" />
      </div>
    </div>
  );
};

// ─── Date group header ────────────────────────────────────────────────────────
const DateGroupHeader = ({ dateKey, trips, theme, isOpen, onToggle }) => {
  const bizCount  = trips.filter(t => t.category === 'business').length;
  const privCount = trips.filter(t => t.category === 'private').length;
  const totalKm   = trips.reduce((s, t) => s + parseFloat(t.distance_km || 0), 0);

  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition-colors"
      style={{ ':hover': {} }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.background}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${PRIMARY}18` }}>
        <Calendar size={13} style={{ color: PRIMARY }} />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-[13px] sm:text-sm font-semibold" style={{ color: theme.text }}>
          {fmtDateLabel(dateKey)}
        </p>
        <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5">
          <span className="text-[9px] sm:text-[10px]" style={{ color: theme.textMuted }}>
            {trips.length} trip{trips.length !== 1 ? 's' : ''}
          </span>
          <span className="text-[9px] sm:text-[10px]" style={{ color: theme.textMuted }}>
            {totalKm.toFixed(1)} km
          </span>
          {bizCount > 0 && (
            <span className="text-[9px] sm:text-[10px] font-medium" style={{ color: BIZ_COLOR }}>
              {bizCount} biz
            </span>
          )}
          {privCount > 0 && (
            <span className="text-[9px] sm:text-[10px] font-medium" style={{ color: PRIV_COLOR }}>
              {privCount} priv
            </span>
          )}
        </div>
      </div>
      <ChevronDown
        size={16}
        style={{
          color: theme.textMuted,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          flexShrink: 0,
        }}
      />
    </button>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pagination = ({ page, pages, total, theme, setPage, compact }) => {
  if (pages <= 1) return null;
  return (
    <div className={`flex items-center justify-between ${compact ? '' : 'mt-6 pt-4'}`}>
      <p className="text-[10px] sm:text-xs" style={{ color: theme.textMuted }}>
        {total} trips · page {page}/{pages}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="p-1.5 rounded-lg disabled:opacity-30 transition-all hover:scale-105 active:scale-95"
          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
        >
          <ChevronLeft size={14} style={{ color: theme.text }} />
        </button>
        <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 font-medium" style={{ color: theme.text }}>
          {page}/{pages}
        </span>
        <button
          onClick={() => setPage(p => Math.min(pages, p + 1))}
          disabled={page >= pages}
          className="p-1.5 rounded-lg disabled:opacity-30 transition-all hover:scale-105 active:scale-95"
          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
        >
          <ChevronRight size={14} style={{ color: theme.text }} />
        </button>
      </div>
    </div>
  );
};



// ─── Auto-classify modal ─────────────────────────────────────────────────────
const AutoClassifyModal = ({ preview, applying, scope, theme, onConfirm, onClose, onScopeChange }) => {
  const bizCount  = preview.filter(r => r.category === 'business').length;
  const privCount = preview.filter(r => r.category === 'private').length;

  // Group by reason label
  const reasonGroups = preview.reduce((acc, r) => {
    acc[r.reasonLabel] = (acc[r.reasonLabel] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl p-5 shadow-2xl"
        style={{ backgroundColor: theme.card }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: theme.text }}>Auto Classify</h3>
          <button onClick={onClose} className="p-1 rounded-lg transition-opacity hover:opacity-70"
            style={{ color: theme.textMuted }}>
            <X size={16} />
          </button>
        </div>

        {/* Scope toggle */}
        <div className="flex rounded-xl p-1 mb-4" style={{ backgroundColor: theme.background }}>
          {['new', 'all'].map(s => (
            <button key={s}
              onClick={() => onScopeChange(s)}
              className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{
                backgroundColor: scope === s ? theme.card : 'transparent',
                color: scope === s ? theme.text : theme.textMuted,
                boxShadow: scope === s ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              {s === 'new' ? 'Unclassified only' : 'All trips'}
            </button>
          ))}
        </div>

        {/* Description */}
        <p className="text-xs mb-4" style={{ color: theme.textMuted, lineHeight: '1.6' }}>
          {preview.length} {preview.length === 1 ? 'trip' : 'trips'} will be classified based on your work schedule, zones and SA public holidays.
          {scope === 'all' ? ' Existing classifications will be overwritten.' : ''}
        </p>

        {/* Biz / Priv pills */}
        {preview.length > 0 && (
          <div className="flex gap-2.5 mb-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
              style={{ backgroundColor: `${BIZ_COLOR}18` }}>
              <Briefcase size={13} style={{ color: BIZ_COLOR }} />
              <span className="text-xs font-bold" style={{ color: BIZ_COLOR }}>{bizCount} Business</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
              style={{ backgroundColor: `${PRIV_COLOR}18` }}>
              <User size={13} style={{ color: PRIV_COLOR }} />
              <span className="text-xs font-bold" style={{ color: PRIV_COLOR }}>{privCount} Private</span>
            </div>
          </div>
        )}

        {/* Reason breakdown */}
        {Object.keys(reasonGroups).length > 0 && (
          <div className="max-h-36 overflow-y-auto mb-4" style={{ scrollbarWidth: 'thin' }}>
            {Object.entries(reasonGroups).map(([reason, count]) => (
              <div key={reason} className="flex justify-between py-1.5 text-xs"
                style={{ borderBottom: `1px solid ${theme.border}` }}>
                <span style={{ color: theme.textMuted }}>{reason}</span>
                <span className="font-semibold" style={{ color: theme.text }}>{count}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <button
          onClick={onConfirm}
          disabled={applying || preview.length === 0}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all active:scale-95 mb-2"
          style={{ backgroundColor: PRIMARY }}
        >
          {applying ? (
            <><Loader2 size={16} className="animate-spin" /> Applying...</>
          ) : (
            <>Apply to {preview.length} trips</>
          )}
        </button>
        <button onClick={onClose} disabled={applying}
          className="w-full py-2 text-sm transition-opacity hover:opacity-70"
          style={{ color: theme.textMuted }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Trips() {
  const theme    = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tripList,   setTripList]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [openDates,  setOpenDates]  = useState({});
  const [showDateFilters, setShowDateFilters] = useState(false);

  // Filters
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [category,  setCategory]  = useState('');

  // Auto-classify
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [autoPreview,   setAutoPreview]   = useState([]);
  const [autoScope,     setAutoScope]     = useState('new');
  const [applying,      setApplying]      = useState(false);
  const [workSchedule,  setWorkSchedule]  = useState([]);
  const [dbReasons,     setDbReasons]     = useState([]);
  const [userZones,     setUserZones]     = useState([]);
  const [userPeriods,   setUserPeriods]   = useState([]);

  // Load supporting data once on mount
  useEffect(() => {
    async function loadClassifyData() {
      try {
        const [schedRes, zonesRes, reasonsRes, periodsRes] = await Promise.all([
          userApi.getSchedule(),
          zonesApi.list(),
          reasonsApi.list(),
          userApi.getPeriods(),
        ]);
        if (schedRes.success)   setWorkSchedule(schedRes.data || []);
        if (zonesRes.success)   setUserZones(zonesRes.data || []);
        if (reasonsRes.success) setDbReasons(reasonsRes.data || []);
        if (periodsRes.success) setUserPeriods(periodsRes.data || []);
      } catch {}
    }
    loadClassifyData();
  }, []);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    const params = { page, status: 'completed' };
    if (dateFrom)  params.date_from = dateFrom;
    if (dateTo)    params.date_to   = dateTo;
    if (category)  params.category  = category;

    const res = await tripsApi.list(params);

    if (res.success) {
      // Response::paginated puts trips in res.data, pagination in res.meta.pagination
      setTripList(res.data || []);
      setTotal(res.meta?.pagination?.total || 0);
      setPages(res.meta?.pagination?.total_pages || 1);

      // Auto-open the most recent date group
      const data = res.data || [];
      if (data.length > 0) {
        const d   = new Date(data[0].start_time);
        const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        setOpenDates(prev => ({ ...prev, [key]: true }));
      }
    }
    setLoading(false);
  }, [page, dateFrom, dateTo, category]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, category]);

  const dateGroups = useMemo(() => groupByDate(tripList), [tripList]);
  const hasFilters = !!(dateFrom || dateTo || category);
  const unclassifiedCount = useMemo(() => tripList.filter(t => !t.category).length, [tripList]);

  // Open modal — compute preview based on scope
  function openAutoClassify(scope = 'new') {
    const source  = scope === 'new' ? tripList.filter(t => !t.category) : tripList;
    const preview = autoClassifyAll(source, workSchedule, userZones, userPeriods);
    setAutoScope(scope);
    setAutoPreview(preview);
    setShowAutoModal(true);
  }

  // Scope toggle inside modal — recompute preview
  function handleScopeChange(scope) {
    const source  = scope === 'new' ? tripList.filter(t => !t.category) : tripList;
    const preview = autoClassifyAll(source, workSchedule, userZones, userPeriods);
    setAutoScope(scope);
    setAutoPreview(preview);
  }

  // Apply classification to server
  async function applyAutoClassify() {
    setApplying(true);
    try {
      // Optimistic update
      const map = Object.fromEntries(autoPreview.map(r => [r.tripId, r.category]));
      setTripList(prev => prev.map(t => map[t.id] ? { ...t, category: map[t.id] } : t));

      for (const r of autoPreview) {
        const trip = tripList.find(t => t.id === r.tripId);
        const payload = { category: r.category };
        if (!trip?.reason_id) payload.reason_id = resolveReasonId(r.reasonLabel, r.category, dbReasons);
        if (r.reason_detail && !trip?.reason_detail) payload.reason_detail = r.reason_detail;
        await tripsApi.update(r.tripId, payload);
      }

      setShowAutoModal(false);
      fetchTrips();
    } catch (err) {
      console.error('Auto-classify failed:', err);
      fetchTrips();
    } finally {
      setApplying(false);
    }
  }

  const toggleDate = (key) =>
    setOpenDates(prev => ({ ...prev, [key]: !prev[key] }));

  const catFilters = [
    { value: '',         label: 'All' },
    { value: 'business', label: 'Business' },
    { value: 'private',  label: 'Private' },
  ];

  const filterBtnStyle = (val) => ({
    padding: '6px 12px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 500,
    border: `1px solid ${category === val
      ? (val === 'business' ? BIZ_COLOR : val === 'private' ? PRIV_COLOR : PRIMARY)
      : theme.border}`,
    backgroundColor: category === val
      ? (val === 'business' ? `${BIZ_COLOR}18` : val === 'private' ? `${PRIV_COLOR}18` : `${PRIMARY}18`)
      : 'transparent',
    color: category === val
      ? (val === 'business' ? BIZ_COLOR : val === 'private' ? PRIV_COLOR : PRIMARY)
      : theme.textMuted,
    cursor: 'pointer',
    flexShrink: 0,
  });

  const inputStyle = {
    width: '100%', padding: '6px 10px', borderRadius: 10,
    border: `1px solid ${theme.border}`, fontSize: 11, outline: 'none',
    backgroundColor: theme.background, color: theme.text,
  };

  return (
    <div className="relative px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 min-h-screen"
      style={{ backgroundColor: theme.background }}>
      <div className="relative z-[1]">

        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: theme.text }}>
              Trips
            </h1>
            {!loading && (
              <p className="text-[11px] sm:text-sm mt-0.5 sm:mt-1" style={{ color: theme.textMuted }}>
                {total} trips
                {dateGroups.length > 0 && (() => {
                  const biz  = tripList.filter(t => t.category === 'business').length;
                  const priv = tripList.filter(t => t.category === 'private').length;
                  const km   = tripList.reduce((s, t) => s + parseFloat(t.distance_km || 0), 0);
                  return (
                    <>
                      {' · '}{km.toFixed(1)} km
                      {biz > 0  && <span style={{ color: BIZ_COLOR,  fontWeight: 500 }}> · {biz} biz</span>}
                      {priv > 0 && <span style={{ color: PRIV_COLOR, fontWeight: 500 }}> · {priv} priv</span>}
                    </>
                  );
                })()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => openAutoClassify('new')}
              disabled={tripList.length === 0}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[10px] sm:text-xs font-semibold disabled:opacity-30 transition-all hover:scale-105 active:scale-95"
              style={{ border: `1.5px solid ${PRIMARY}40`, color: PRIMARY, backgroundColor: `${PRIMARY}08` }}
            >
              <Sparkles size={13} />
              <span className="hidden sm:inline">Classify</span>
              {unclassifiedCount > 0 && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
                  style={{ backgroundColor: PRIMARY, minWidth: 18, textAlign: 'center' }}>
                  {unclassifiedCount}
                </span>
              )}
            </button>
            </div>
        </div>

        {/* Two-column layout — left filters (desktop) + right trip list */}
        <div className="md:flex md:gap-6 md:items-start">

          {/* LEFT PANEL — sticky on desktop */}
          <div className="md:w-64 md:flex-shrink-0 md:sticky md:top-6">

            {/* Filters card */}
            <Card theme={theme} className="p-3 sm:p-4 mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                style={{ color: theme.textMuted }}>Category</p>
              <div className="flex md:flex-col gap-1.5 flex-wrap mb-4">
                {catFilters.map(f => (
                  <button key={f.value}
                    onClick={() => { setCategory(f.value); setPage(1); }}
                    style={filterBtnStyle(f.value)}>
                    {f.label}
                  </button>
                ))}
              </div>

              <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                style={{ color: theme.textMuted }}>Date range</p>
              <div className="space-y-2">
                <div>
                  <label className="text-[10px] mb-1 block" style={{ color: theme.textMuted }}>From</label>
                  <input type="date" value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                    style={inputStyle} />
                </div>
                <div>
                  <label className="text-[10px] mb-1 block" style={{ color: theme.textMuted }}>To</label>
                  <input type="date" value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                    style={inputStyle} />
                </div>
                {hasFilters && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); setCategory(''); setPage(1); }}
                    className="text-[10px] font-medium" style={{ color: PRIMARY }}>
                    Clear filters
                  </button>
                )}
              </div>
            </Card>
          </div>

          {/* RIGHT PANEL — trip list */}
          <div className="flex-1 min-w-0">

            {/* Mobile filters */}
            <div className="md:hidden">
              <Card theme={theme} className="p-3 mb-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {catFilters.map(f => (
                    <button key={f.value}
                      onClick={() => { setCategory(f.value); setPage(1); }}
                      style={{ ...filterBtnStyle(f.value), padding: '4px 10px', fontSize: 10 }}>
                      {f.label}
                    </button>
                  ))}
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() => setShowDateFilters(!showDateFilters)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-medium relative"
                    style={{ border: `1px solid ${theme.border}`, color: theme.textSecondary, backgroundColor: 'transparent' }}>
                    <Calendar size={11} />
                    Dates
                    {(dateFrom || dateTo) && (
                      <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-white flex items-center justify-center"
                        style={{ fontSize: 8, backgroundColor: PRIMARY }}>
                        {[dateFrom, dateTo].filter(Boolean).length}
                      </span>
                    )}
                  </button>
                </div>
                {showDateFilters && (
                  <div className="flex items-end gap-2 mt-3 pt-3"
                    style={{ borderTop: `1px solid ${theme.border}` }}>
                    <div style={{ flex: 1 }}>
                      <label className="text-[10px] mb-1 block" style={{ color: theme.textMuted }}>From</label>
                      <input type="date" value={dateFrom}
                        onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                        style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="text-[10px] mb-1 block" style={{ color: theme.textMuted }}>To</label>
                      <input type="date" value={dateTo}
                        onChange={e => { setDateTo(e.target.value); setPage(1); }}
                        style={inputStyle} />
                    </div>
                    {(dateFrom || dateTo) && (
                      <button onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                        className="text-[10px] font-medium shrink-0" style={{ color: PRIMARY }}>
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </Card>
            </div>

            {/* Pagination top */}
            <div className="flex items-center justify-end mb-3">
              <Pagination page={page} pages={pages} total={total} theme={theme} setPage={setPage} compact />
            </div>

            {/* Trip list */}
            {loading ? (
              <div className="py-16 text-center">
                <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3"
                  style={{ borderColor: theme.border, borderTopColor: PRIMARY }} />
                <p className="text-sm" style={{ color: theme.textMuted }}>Loading trips...</p>
              </div>
            ) : tripList.length === 0 ? (
              <Card theme={theme} className="py-16 text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${PRIMARY}18` }}>
                  <MapPin size={24} style={{ color: PRIMARY }} />
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: theme.text }}>No trips found</p>
                <p className="text-xs px-8" style={{ color: theme.textMuted }}>
                  {hasFilters ? 'Try adjusting your filters.' : 'Trips recorded in the app will appear here.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-1">
                {dateGroups.map(([dateKey, dateTrips]) => (
                  <div key={dateKey}>
                    <DateGroupHeader
                      dateKey={dateKey}
                      trips={dateTrips}
                      theme={theme}
                      isOpen={!!openDates[dateKey]}
                      onToggle={() => toggleDate(dateKey)}
                    />
                    {openDates[dateKey] && (
                      <div className="ml-3 sm:ml-4 pl-3 sm:pl-4 space-y-1.5 mt-1 mb-2"
                        style={{ borderLeft: `2px solid ${theme.border}` }}>
                        {dateTrips.map(trip => (
                          <TripCard
                            key={trip.id}
                            trip={trip}
                            theme={theme}
                            onClick={() => navigate(`/portal/trips/${trip.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <Pagination page={page} pages={pages} total={total} theme={theme} setPage={setPage} />
          </div>
        </div>
      </div>

      <BackToTop theme={theme} />

     

      {showAutoModal && (
        <AutoClassifyModal
          preview={autoPreview}
          applying={applying}
          scope={autoScope}
          theme={theme}
          onConfirm={applyAutoClassify}
          onClose={() => setShowAutoModal(false)}
          onScopeChange={handleScopeChange}
        />
      )}

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slide-down { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 200px; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-slide-down { animation: slide-down 0.2s ease-out; overflow: hidden; }
      `}</style>
    </div>
  );
}